import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { formatAmount } from '../money';
import { getHistory } from './api';
import { formatWhen } from './format';
import type { HistoryItem } from './types';

type State =
  { status: 'loading' } | { status: 'error' } | { status: 'ready'; items: HistoryItem[] };

type Translate = (key: string, options?: object) => string;

function actionLabel(item: HistoryItem, t: Translate): string {
  const amount = item.details?.['amount'];
  return t(`history.action.${item.action}`, {
    amount: typeof amount === 'string' ? formatAmount(amount) : '',
    defaultValue: item.action,
  });
}

function detailLine(item: HistoryItem, t: Translate): string | null {
  if (item.kind === 'payment') {
    const reference = item.details?.['reference'];
    const paidOn = item.details?.['paidOn'];
    if (typeof reference !== 'string') return null;
    return t('history.paymentRef', {
      reference,
      date: typeof paidOn === 'string' ? paidOn.split('-').reverse().join('/') : '',
    });
  }
  const days = item.details?.['days'];
  if (Array.isArray(days) && days.length > 0) {
    // The flat-rate allowance's edits name the days they added or removed, and a removal by
    // Gestione or an approver its reason (decision 203).
    const named = days.map((day) => String(day).split('-').reverse().join('/')).join(', ');
    const reason = item.details?.['reason'];
    return typeof reason === 'string' && reason !== '' ? `${named} — «${reason}»` : named;
  }
  return item.note ? `«${item.note}»` : null;
}

/**
 * «Storia» (spec §1.3): the report's trail, oldest first and newest last — decisions with their
 * notes, edits, and payments — from `GET …/history`, the same pattern as the expense's history.
 * `revision` reloads it after a write.
 */
export function ReportHistory({
  reportId,
  revision,
}: {
  readonly reportId: string;
  readonly revision: string;
}) {
  const { t } = useTranslation('closing');
  const tx = t as Translate;
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    void getHistory(reportId).then((answer) => {
      if (!active) return;
      setState(
        answer.ok ? { status: 'ready', items: answer.value.items ?? [] } : { status: 'error' },
      );
    });
    return () => {
      active = false;
    };
  }, [reportId, revision]);

  return (
    <section
      aria-labelledby={`history-${reportId}`}
      className="border-border flex flex-col gap-2 border-t pt-3"
    >
      <h3 id={`history-${reportId}`} className="text-sm font-semibold">
        {t('history.title')}
      </h3>
      {state.status === 'loading' ? <Skeleton className="h-10 w-full" /> : null}
      {state.status === 'error' ? (
        <p className="text-muted-foreground text-sm">{t('history.loadError')}</p>
      ) : null}
      {state.status === 'ready' && state.items.length === 0 ? (
        <p className="text-muted-foreground text-sm">{t('history.empty')}</p>
      ) : null}
      {state.status === 'ready' && state.items.length > 0 ? (
        <ol className="flex flex-col gap-2 text-sm">
          {state.items.map((item, index) => (
            <li key={`${item.at}-${index}`} className="flex flex-col gap-0.5">
              <b>{actionLabel(item, tx)}</b>
              <span className="text-muted-foreground">
                {[formatWhen(item.at), item.actor?.displayName ?? t('history.system')].join(' · ')}
                {detailLine(item, tx) ? ` — ${detailLine(item, tx)}` : ''}
              </span>
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}
